import React, { useState, useEffect, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "../../../utils/api";
import { motion, AnimatePresence } from "framer-motion";
import Swal from "sweetalert2";
import {
    MessageCircle,
    Send,
    Calendar,
    Paperclip,
    Search,
} from "lucide-react";
import { format } from "date-fns";

export default function RequestChat() {
    const queryClient = useQueryClient();
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [newNote, setNewNote] = useState("");
    const [searchTerm, setSearchTerm] = useState("");
    const [, setIsAddNoteOpen] = useState(false);
    const messagesEndRef = useRef(null);

    // Fetch paralegal requests
    const { data: requests = [], isLoading: loadingRequests } = useQuery({
        queryKey: ["paralegal-requests"],
        queryFn: async () => {
            const token = localStorage.getItem("auth_token");
            if (!token) throw new Error("Authentication required");
            const res = await api.get("/requests/paralegal-requests", {
                headers: { Authorization: `Bearer ${token}` },
            });
            return res.data.data;
        },
    });

    // Fetch notes for selected request
    const { data: noteData, isLoading: loadingNotes } = useQuery({
        queryKey: ["request-notes", selectedRequest?.id],
        queryFn: async () => {
            const token = localStorage.getItem("auth_token");
            const res = await api.get(`/requests/${selectedRequest.id}/notes`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            return res.data.data || [];
        },
        enabled: !!selectedRequest,
    });

    // Add note mutation
    const addNoteMutation = useMutation({
        mutationFn: async (note) => {
            const token = localStorage.getItem("auth_token");
            const res = await api.post(`/requests/${selectedRequest.id}/notes`,
                { note },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            return res.data.data;
        },
        onSuccess: (newNote) => {
            queryClient.setQueryData(["request-notes", selectedRequest.id], (old) => [...old, newNote]);
            setNewNote("");
            setIsAddNoteOpen(false);
            Swal.fire({
                icon: "success",
                title: "Note Added!",
                text: "Your note has been saved successfully.",
                timer: 2000,
                showConfirmButton: false,
            });
        },
        onError: (err) => {
            Swal.fire({
                icon: "error",
                title: "Failed",
                text: err.response?.data?.message || "Could not add note.",
            });
        },
    });

    // Auto-scroll to bottom when notes update
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [noteData]);

    // Filter requests
    const filteredRequests = requests.filter(req =>
        req.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        req.client_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        req.form_name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const handleAddNote = () => {
        if (!newNote.trim()) return;
        addNoteMutation.mutate(newNote);
    };

    if (loadingRequests) {
        return (
            <section className="p-6">
                <div className="flex justify-center items-center py-16">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                    <p className="ml-3 text-gray-600">Loading requests...</p>
                </div>
            </section>
        );
    }

    return (
        <section className="p-6 h-full flex flex-col lg:flex-row gap-6">
            {/* Requests Sidebar */}
            <div className="h-[80vh] lg:w-80 bg-white rounded-xl shadow-sm border border-gray-200 p-5 flex flex-col h-full lg:h-auto">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-bold text-gray-900">My Requests</h3>
                    <span className="text-sm text-gray-500">{requests.length} total</span>
                </div>

                {/* Search */}
                <div className="relative mb-4">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                        type="text"
                        placeholder="Search requests..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    />
                </div>

                {/* Requests List */}
                <div className="flex-1 overflow-y-auto space-y-2">
                    <AnimatePresence>
                        {filteredRequests.length === 0 ? (
                            <p className="text-center text-gray-500 py-8">No requests found</p>
                        ) : (
                            filteredRequests.map((req) => (
                                <motion.div
                                    key={req.id}
                                    layout
                                    initial={{ opacity: 0, x: -20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    exit={{ opacity: 0, x: 20 }}
                                    onClick={() => setSelectedRequest(req)}
                                    className={`p-3 rounded-lg cursor-pointer transition-all ${selectedRequest?.id === req.id
                                        ? "bg-blue-50 border-2 border-blue-500"
                                        : "bg-gray-50 border-2 border-transparent hover:bg-gray-100"
                                        }`}
                                >
                                    <h4 className="font-semibold text-gray-900 text-sm">{req.title}</h4>
                                    <p className="text-xs text-gray-600 mt-1 line-clamp-1">
                                        {req.client_name} • {req.form_name}
                                    </p>
                                    <div className="flex items-center gap-2 mt-2 text-xs text-gray-500">
                                        <Paperclip className="w-3 h-3" />
                                        <span>{req.files_count} file(s)</span>
                                    </div>
                                </motion.div>
                            ))
                        )}
                    </AnimatePresence>
                </div>
            </div>

            {/* Chat Area */}
            <div className="h-[80vh] flex-1 bg-white rounded-xl shadow-sm border border-gray-200 flex flex-col h-full">
                {!selectedRequest ? (
                    <div className="flex-1 flex flex-col items-center justify-center text-gray-400 p-8">
                        <MessageCircle className="w-16 h-16 mb-4" />
                        <p className="text-lg font-medium">Select a request to view notes</p>
                        <p className="text-sm mt-1">Choose from the list on the left</p>
                    </div>
                ) : (
                    <>
                        {/* Request Header */}
                        <div className="p-5 border-b border-gray-200">
                            <div className="flex items-center justify-between">
                                <div>
                                    <h3 className="text-xl font-bold text-gray-900">{selectedRequest.title}</h3>
                                    <p className="text-sm text-gray-600 mt-1">
                                        {selectedRequest.client_name} • Form: {selectedRequest.form_name}
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Notes Chat */}
                        <div className="flex-1 overflow-y-auto p-5 space-y-4">
                            {loadingNotes ? (
                                <div className="flex justify-center py-8">
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                                </div>
                            ) : noteData?.length === 0 ? (
                                <div className="text-center py-12 text-gray-400">
                                    <MessageCircle className="w-12 h-12 mx-auto mb-3" />
                                    <p>No notes yet</p>
                                    <p className="text-sm mt-1">Start by adding your first note</p>
                                </div>
                            ) : (
                                <AnimatePresence>
                                    {noteData.map((note, index) => (
                                        <motion.div
                                            key={note.id || index}
                                            initial={{ opacity: 0, y: 20 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            className="flex gap-3"
                                        >
                                            <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center text-white font-bold text-sm">
                                                {note.created_by?.charAt(0).toUpperCase() || "P"}
                                            </div>
                                            <div className="flex-1">
                                                <div className="bg-gray-50 rounded-2xl px-4 py-3 max-w-2xl">
                                                    <p className="text-gray-800 whitespace-pre-wrap break-all">{note.note}</p>
                                                </div>
                                                <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
                                                    <span className="font-medium">{note.added_by.role}</span>
                                                    <span>•</span>
                                                    <span className="flex items-center gap-1">
                                                        <Calendar className="w-3 h-3" />
                                                        {format(new Date(note.created_at), "MMM d, yyyy 'at' h:mm a")}
                                                    </span>
                                                </div>
                                            </div>
                                        </motion.div>
                                    ))}
                                </AnimatePresence>
                            )}
                            <div ref={messagesEndRef} />
                        </div>

                        {/* Quick Add (bottom input) */}
                        <div className="p-4 border-t border-gray-200">
                            <div className="flex gap-2">
                                <input
                                    type="text"
                                    value={newNote}
                                    onChange={(e) => setNewNote(e.target.value)}
                                    onKeyPress={(e) => e.key === "Enter" && handleAddNote()}
                                    placeholder="Type a quick note..."
                                    className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                />
                                <button
                                    onClick={handleAddNote}
                                    disabled={!newNote.trim() || addNoteMutation.isPending}
                                    className="px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors flex items-center gap-2"
                                >
                                    <Send className="w-4 h-4" />
                                    Send
                                </button>
                            </div>
                        </div>
                    </>
                )}
            </div>

            {/* Custom CSS for SweetAlert */}
            <style jsx>{`
        .swal-custom-note .swal2-textarea {
          border: 1px solid #d1d5db;
          border-radius: 0.5rem;
          padding: 0.75rem;
          font-size: 0.875rem;
          width: 100%;
        }
        .swal-custom-note .swal2-textarea:focus {
          outline: none;
          ring: 2px solid #3b82f6;
          border-color: #3b82f6;
        }
        .swal-confirm-btn {
          background-color: #2563eb !important;
          color: white !important;
        }
        .swal-cancel-btn {
          background-color: #6b7280 !important;
          color: white !important;
        }
      `}</style>
        </section>
    );
}